import type { INoteRepository } from "@/application/ports/repositories/INoteRepository";
import type { EntityRef } from "@/domain/entityRef";
import type {
  NoteList,
  NoteListParams,
  NoteSave,
  NoteSaved,
  NoteScope,
  RecordNote,
} from "@/domain/note";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

export class NoteRepository implements INoteRepository {
  async list(params: NoteListParams, signal?: AbortSignal): Promise<NoteList> {
    return unwrap(sdk.listNotes({ query: params, signal }));
  }
  async aggregates(scope: NoteScope, signal?: AbortSignal): Promise<{ total: number }> {
    return unwrap(sdk.aggregateNotes({ query: scope, signal }));
  }
  async create(holder: EntityRef): Promise<RecordNote> {
    return unwrap(sdk.createNote({ body: { holder } }));
  }
  async get(uuid: string, signal?: AbortSignal): Promise<RecordNote> {
    return unwrap(sdk.getNote({ path: { note_uuid: uuid }, signal }));
  }
  async save(uuid: string, changes: NoteSave): Promise<NoteSaved> {
    const result = await sdk.saveNote({ path: { note_uuid: uuid }, body: changes });
    if (
      result.response?.status === 409 &&
      typeof result.error === "object" &&
      result.error !== null &&
      "current_revision" in result.error
    ) {
      return unwrap<NoteSaved>(
        Promise.resolve({
          ...result,
          error: {
            code: "note_revision_conflict",
            message: "The note has changed",
            details: { current_revision: result.error.current_revision },
          },
        }),
      );
    }
    return unwrap(Promise.resolve(result));
  }
  async delete(uuid: string): Promise<void> {
    await unwrap(sdk.deleteNote({ path: { note_uuid: uuid } }));
  }
}
